import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { AlertTriangle, MessageCircleQuestion } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EmbeddedLoadingChart } from "@/components/ui/embedded-loading-chart";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { cn } from "@/lib/utils";
import {
  getStrategyRdHypothesis,
  pollStrategyRdRobustness,
  runStrategyRdRobustness,
} from "../../../services/strategyRdApi";
import { ApiRequestError } from "../../../utils/apiRequestError";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import {
  formatStrategyRdMoney,
  formatStrategyRdPct,
  strategyRdOutcomeLabel,
} from "../../../utils/strategyRd";
import type { StrategyRdFragileTrade, StrategyRdRobustness } from "../../../types/strategyRd";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { strategyRdBrandActionButtonClassName } from "./strategyRdCardShell";
import {
  StrategyRdBadge,
  StrategyRdFacts,
  StrategyRdInfo,
  StrategyRdLabCard,
  StrategyRdNote,
  StrategyRdStat,
  StrategyRdStatGrid,
  StrategyRdSubsection,
  strategyRdBodyTextClassName,
  strategyRdCaptionClassName,
  type StrategyRdTone,
} from "./strategyRdLabUi";
import { HeroStrip, type StrategyRdHeroCard } from "./StrategyRdOverviewStats";
import {
  MINI_STAT_COUNT_GRADIENT,
  MINI_STAT_LOSS_GRADIENT,
  MINI_STAT_OPEN_GRADIENT,
  MINI_STAT_WIN_GRADIENT,
} from "../../../utils/miniStatGradient";

const NEEDS_BACKTEST = "strategy_rd.robustness_needs_backtest";
const DATA_LOADING = "strategy_rd.robustness_data_loading";
const RUNNING_LABEL = "Testing against feed differences…";
/** Shown for a missing figure (the Statistics "N/A"). */
const NA = "N/A";


/** A queued / running test for this backtest — keep polling its job. */
function pendingJobId(rb: StrategyRdRobustness | null, appliedAt: string | null): string | null {
  if (!rb || !appliedAt || rb.applied_at !== appliedAt) return null;
  if (rb.status !== "queued" && rb.status !== "running") return null;
  return rb.job_id?.trim() || null;
}

/** The finished result, only when it belongs to the current backtest. */
function currentResult(
  rb: StrategyRdRobustness | null,
  appliedAt: string | null
): StrategyRdRobustness | null {
  if (!rb || !appliedAt || rb.applied_at !== appliedAt || rb.superseded) return null;
  if (rb.status === "ready" && rb.verdict) return rb;
  if (rb.status === "failed") return rb;
  return null;
}

function pct(value: number | null | undefined): string {
  return formatStrategyRdPct(value) ?? NA;
}

/** A 0 to 1 share as a whole percent. */
function share(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? `${Math.round(value * 100)}%` : NA;
}

export type StrategyRdRobustnessPanelProps = {
  hypothesisId: string | null;
  /**
   * `last_apply.applied_at` of the backtest on screen. Omit with
   * `loadFromHypothesis` to read it (and `last_robustness`) from the hypothesis.
   */
  appliedAt?: string | null;
  /** A known test state (Confirm's automatic test, or `last_robustness`). */
  initial?: StrategyRdRobustness | null;
  /** GET the hypothesis on mount for `last_apply` + `last_robustness` (strategy page). */
  loadFromHypothesis?: boolean;
  timeZone: string;
  currency?: string | null;
  /** Fragile trades are clickable (jump to the trade on the chart). */
  onSelectFragile?: (trade: StrategyRdFragileTrade) => void;
  /** Current fragile trades, for chart markers (empty when no current result). */
  onFragileTradesChange?: (trades: StrategyRdFragileTrade[]) => void;
  /** The finished result for the current backtest, or null (none / other backtest). */
  onResultChange?: (result: StrategyRdRobustness | null) => void;
  /** Bump to start the test from outside (warning action, Save dialog). */
  runToken?: number;
  /** Opens TES with a question about the result. */
  onAskTes?: (question: string) => void;
};

/**
 * Price-noise robustness test: on demand, and once automatically when a recipe is
 * saved. Shows a saved result only when it belongs to the current backtest.
 */
export function StrategyRdRobustnessPanel({
  hypothesisId,
  appliedAt: appliedAtProp = null,
  initial = null,
  loadFromHypothesis = false,
  timeZone,
  currency = null,
  onSelectFragile,
  onFragileTradesChange,
  onResultChange,
  runToken = 0,
  onAskTes,
}: StrategyRdRobustnessPanelProps): JSX.Element | null {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [loadedAppliedAt, setLoadedAppliedAt] = useState<string | null>(null);
  const appliedAt = loadFromHypothesis ? loadedAppliedAt : appliedAtProp;
  const [robustness, setRobustness] = useState<StrategyRdRobustness | null>(initial);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsBacktest, setNeedsBacktest] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (initial) setRobustness(initial);
  }, [initial]);

  // Strategy page: the hypothesis holds the backtest stamp and the saved test.
  useEffect(() => {
    if (!loadFromHypothesis || !hypothesisId) return;
    const controller = new AbortController();
    void getStrategyRdHypothesis(hypothesisId, controller.signal)
      .then((hyp) => {
        if (controller.signal.aborted) return;
        setLoadedAppliedAt(hyp.last_apply?.applied_at ?? null);
        setNeedsBacktest(!hyp.last_apply?.applied_at);
        // Confirm's queued state carries no backtest stamp; the saved one does.
        const stamp = hyp.last_apply?.applied_at ?? null;
        setRobustness(
          (prev) =>
            hyp.last_robustness ?? (prev ? { ...prev, applied_at: prev.applied_at ?? stamp } : null)
        );
      })
      .catch(() => {
        // Hypothesis gone or Lab off: the panel just offers nothing.
      });
    return () => controller.abort();
  }, [hypothesisId, loadFromHypothesis]);

  const track = useCallback(
    async (start: (signal: AbortSignal) => Promise<StrategyRdRobustness | null>) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setRunning(true);
      setError(null);
      try {
        const out = await start(controller.signal);
        if (controller.signal.aborted) return;
        setRobustness(out);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiRequestError && err.slug === NEEDS_BACKTEST) {
          setNeedsBacktest(true);
        }
        if (err instanceof ApiRequestError && err.slug === DATA_LOADING) {
          setError(`${err.message} Try again in a moment.`);
        } else {
          setError(err instanceof Error ? err.message : "Robustness test failed.");
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setRunning(false);
        }
      }
    },
    []
  );

  // Resume a test already queued / running for this backtest (reload, or Confirm).
  const jobToResume = pendingJobId(robustness, appliedAt);
  useEffect(() => {
    if (!jobToResume) return;
    void track((signal) => pollStrategyRdRobustness(jobToResume, signal));
  }, [jobToResume, track]);

  // A new backtest (or another hypothesis): stop following the old test.
  useEffect(() => {
    setError(null);
    setNeedsBacktest(false);
    return () => abortRef.current?.abort();
  }, [appliedAt, hypothesisId]);

  const result = currentResult(robustness, appliedAt);
  const ready = result?.status === "ready" ? result : null;
  const fragile = ready?.fragile_trades ?? [];

  useEffect(() => {
    onFragileTradesChange?.(fragile);
    // `fragile` is derived from `ready`; its identity changes only with the result.
  }, [ready, onFragileTradesChange]);
  useEffect(() => () => onFragileTradesChange?.([]), [onFragileTradesChange]);

  useEffect(() => {
    onResultChange?.(ready);
  }, [ready, onResultChange]);
  useEffect(() => () => onResultChange?.(null), [onResultChange]);

  const start = useCallback(() => {
    if (!hypothesisId || running) return;
    void track((signal) => runStrategyRdRobustness(hypothesisId, signal));
  }, [hypothesisId, running, track]);

  // Started from a warning or the Save dialog: run (unless shown / running) and bring it into view.
  const startRef = useRef(start);
  startRef.current = start;
  const readyRef = useRef(ready);
  readyRef.current = ready;
  useEffect(() => {
    if (!runToken) return;
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    if (!readyRef.current) startRef.current();
  }, [runToken]);

  if (!hypothesisId) return null;

  const failedMessage =
    error || (result?.status === "failed" ? result.error?.message || "Robustness test failed." : null);
  const stable = ready?.verdict === "stable";
  const stability = ready?.trade_stability?.median;
  const lo = ready?.return_pct?.p5 ?? null;
  const hi = ready?.return_pct?.p95 ?? null;
  const base = ready?.baseline?.return_pct ?? null;
  const costed = ready?.cost_stress?.variants?.find((v) => v.label === "assumed_costs" || v.label === "double_cost") ?? null;
  const signColor = (v: number | null | undefined) =>
    v == null ? undefined : v >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR;

  const actions = (
    <>
      {ready && onAskTes ? (
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
          className={strategyRdBrandActionButtonClassName}
          onClick={() =>
            onAskTes(
              "Explain this robustness test: how much the result moves on slightly different data and with typical costs, which trades are fragile, and whether I can trust it."
            )
          }
        >
          <MessageCircleQuestion className="size-4" aria-hidden />
          <span className="normal-case tracking-normal">Ask TES</span>
        </Button>
      ) : null}
      {!running ? (
        <Button
          type="button"
          variant={ready ? "outline-brand" : "gradient"}
          size="sm"
          data-font="primary"
          data-strategy-rd-robustness-cta=""
          className={ready ? strategyRdBrandActionButtonClassName : "h-8 rounded-[6px] px-3"}
          disabled={needsBacktest || !appliedAt}
          title={needsBacktest ? "Run a backtest first" : undefined}
          onClick={start}
        >
          <span className="normal-case tracking-normal">{ready ? "Run again" : "Check robustness"}</span>
        </Button>
      ) : null}
    </>
  );

  // The answer first, in the Statistics headline tiles; the evidence behind it in one card.
  const heroes: StrategyRdHeroCard[] = ready
    ? [
        {
          id: "verdict",
          title: "Verdict",
          amount: stable ? "Stable" : "Sensitive",
          backgroundGradient: stable ? MINI_STAT_WIN_GRADIENT : MINI_STAT_OPEN_GRADIENT,
          tooltip: stable
            ? "The result holds on slightly different data."
            : "The result moves a lot on slightly different data. Judge it by the range, not one number.",
        },
        {
          id: "same-trades",
          title: "Same trades",
          amount: share(stability),
          backgroundGradient: MINI_STAT_COUNT_GRADIENT,
          tooltip: "Of every 10 trades, how many come back identical in a typical re-run",
        },
        {
          id: "return-range",
          title: "Return range",
          amount: `${pct(lo)} to ${pct(hi)}`,
          backgroundGradient:
            lo != null && lo >= 0 ? MINI_STAT_WIN_GRADIENT : hi != null && hi < 0 ? MINI_STAT_LOSS_GRADIENT : MINI_STAT_OPEN_GRADIENT,
          tooltip: "Where 9 in 10 re-runs' returns land",
        },
        {
          id: "with-costs",
          title: "With typical costs",
          amount: costed ? pct(costed.return_pct) : NA,
          backgroundGradient:
            costed?.return_pct == null
              ? MINI_STAT_COUNT_GRADIENT
              : costed.return_pct >= 0
                ? MINI_STAT_WIN_GRADIENT
                : MINI_STAT_LOSS_GRADIENT,
          tooltip: "The return after the instrument's usual spread and overnight swap",
        },
      ]
    : [];

  const howItWorks = (
    <StrategyRdInfo title="How this test works">
      <RobustnessStepsText runs={ready?.runs ?? null} />
    </StrategyRdInfo>
  );

  const showResult = Boolean(ready) && !running;
  const fragileTotal = ready?.fragile_count ?? fragile.length;

  return (
    <div ref={rootRef} className="flex flex-col gap-4" data-strategy-rd-robustness="">
      {showResult ? <HeroStrip items={heroes} /> : null}
      <StrategyRdLabCard
        title={
          <>
            Robustness
            {howItWorks}
          </>
        }
        description={
          running
            ? "Re-running this backtest on copies of the data that differ the way two price feeds do, then with typical trading costs."
            : showResult
              ? stable
                ? "The result holds on slightly different data and with typical costs."
                : "The result moves a lot on slightly different data. Judge it by the range, not one number."
              : "The same recipe re-run on copies of the data that differ the way two price feeds do, and with typical trading costs. A strategy you can trust gives a similar answer every time."
        }
        actions={actions}
      >
        {running ? (
          <RobustnessRunning />
        ) : (
          <>
            {failedMessage ? (
              <Alert variant="error" data-strategy-rd-robustness-error="">
                <AlertTriangle aria-hidden />
                <AlertTitle className="font-[family-name:var(--font-primary)] text-sm font-medium">
                  The robustness test did not finish
                </AlertTitle>
                <AlertDescription className="font-[family-name:var(--font-primary)]">{failedMessage}</AlertDescription>
              </Alert>
            ) : null}
            {!ready ? <RobustnessChecks /> : null}
          </>
        )}

        {showResult && ready ? (
          <div className="flex flex-col gap-4" data-strategy-rd-robustness-result="">
            <StrategyRdStatGrid columns={3} variant="cards" className="items-start">
              <StrategyRdStat
                label="Same trades"
                info={
                  <StrategyRdInfo title="Same trades on slightly different data">
                    <p>
                      We re-run the backtest many times on candles nudged by tiny amounts (like a different data feed
                      would give). The share of trades that stay identical shows how sensitive the strategy is to noise
                      in the prices.
                    </p>
                    <p>High means robust. Low means the trades depend on exact prices you won't get live.</p>
                  </StrategyRdInfo>
                }
                value={share(stability)}
                unit="stay the same"
              >
                {stability != null ? <KeptGrid share={stability} /> : null}
                <StrategyRdFacts
                  rows={[
                    ready.runs != null ? { key: "runs", label: "Re-runs", value: String(ready.runs) } : null,
                    ready.trade_stability?.p5 != null
                      ? { key: "p5", label: "Worst 1 in 20 re-runs", value: share(ready.trade_stability.p5) }
                      : null,
                    ready.baseline?.trades != null
                      ? { key: "trades", label: "Trades in this backtest", value: String(ready.baseline.trades) }
                      : null,
                  ]}
                />
              </StrategyRdStat>
              <StrategyRdStat
                label="Return range"
                info={
                  <StrategyRdInfo title="Return range across re-runs">
                    <p>
                      Across the slightly different data re-runs, 9 out of 10 returns fall inside this range. The dot is
                      this backtest. A wide range, or one crossing zero, means the headline number is partly luck of the
                      data.
                    </p>
                  </StrategyRdInfo>
                }
                badge={rangeBadge(lo, hi)}
                value={
                  <>
                    <span style={{ color: signColor(lo) }}>{pct(lo)}</span>
                    <span className="text-base font-medium text-muted-foreground"> to </span>
                    <span style={{ color: signColor(hi) }}>{pct(hi)}</span>
                  </>
                }
              >
                {lo != null && hi != null && hi > lo ? <ReturnLine lo={lo} hi={hi} base={base} /> : null}
                <StrategyRdFacts
                  rows={[
                    { key: "base", label: "This backtest", value: pct(base), valueColor: signColor(base) },
                    ready.return_pct?.median != null
                      ? {
                          key: "median",
                          label: "Typical re-run",
                          value: pct(ready.return_pct.median),
                          valueColor: signColor(ready.return_pct.median),
                        }
                      : null,
                    { key: "span", label: "Covers", value: "9 in 10 re-runs" },
                  ]}
                />
              </StrategyRdStat>
              <StrategyRdStat
                label="With typical costs"
                info={
                  <StrategyRdInfo title="With typical costs">
                    <p>
                      The same trades after the instrument's usual spread and overnight swap. Short-term strategies with
                      many trades often lose their edge here. This is closer to what a broker would actually pay you.
                    </p>
                  </StrategyRdInfo>
                }
                badge={costBadge(costed?.return_pct)}
                value={costed ? pct(costed.return_pct) : NA}
                valueColor={signColor(costed?.return_pct)}
              >
                <CostBars asTested={base} withCosts={costed?.return_pct ?? null} />
                <StrategyRdFacts
                  rows={[
                    { key: "tested", label: "As tested", value: pct(base), valueColor: signColor(base) },
                    {
                      key: "costed",
                      label: "Spread and swap",
                      value: costed ? pct(costed.return_pct) : NA,
                      valueColor: signColor(costed?.return_pct),
                    },
                    costed?.trades != null ? { key: "trades", label: "Trades", value: String(costed.trades) } : null,
                  ]}
                />
              </StrategyRdStat>
            </StrategyRdStatGrid>

            {fragile.length > 0 ? (
              <StrategyRdSubsection
                title="Fragile trades"
                badge={<StrategyRdBadge tone="warn">{fragileTotal} fragile</StrategyRdBadge>}
                description={`Gone in more than a quarter of the re-runs.${onSelectFragile ? " Click a row to show it on the chart." : ""}`}
                info={
                  <StrategyRdInfo title="Fragile trades">
                    <p>
                      Trades that disappear in more than a quarter of the re-runs. They exist only because of the exact
                      prices in this data feed, so expect them to differ live.
                    </p>
                  </StrategyRdInfo>
                }
              >
                <FragileTradeList
                  trades={fragile}
                  total={fragileTotal}
                  timeZone={timeZone}
                  currency={currency}
                  onSelect={onSelectFragile}
                />
              </StrategyRdSubsection>
            ) : null}
            {ready.calibration?.note ? <StrategyRdNote>{ready.calibration.note}</StrategyRdNote> : null}
          </div>
        ) : null}
      </StrategyRdLabCard>
    </div>
  );
}

function rangeBadge(lo: number | null, hi: number | null): JSX.Element | null {
  if (lo == null || hi == null) return null;
  if (lo >= 0) return <StrategyRdBadge tone="good">All re-runs profit</StrategyRdBadge>;
  if (hi < 0) return <StrategyRdBadge tone="bad">All re-runs lose</StrategyRdBadge>;
  return (
    <StrategyRdBadge tone="warn" title="Some re-runs lose money.">
      Crosses zero
    </StrategyRdBadge>
  );
}

function costBadge(v: number | null | undefined): JSX.Element | null {
  if (v == null) return null;
  return v >= 0 ? (
    <StrategyRdBadge tone="good">Still positive</StrategyRdBadge>
  ) : (
    <StrategyRdBadge tone="bad">Loses money</StrategyRdBadge>
  );
}

function robustnessSteps(runs: number | null): { title: string; body: string }[] {
  return [
    {
      title: "Nudge the prices",
      body: `${runs ?? "Many"} copies of the candles, each shifted by the small amounts two data feeds really differ by.`,
    },
    { title: "Re-run everything", body: "The same rules and settings on every copy. Nothing is re-tuned." },
    { title: "Compare", body: "Do the same trades come back? How far does the return move? What do typical costs take?" },
  ];
}

/** The three steps as text, for the "How this test works" popover. */
function RobustnessStepsText({ runs }: { runs: number | null }): JSX.Element {
  return (
    <ol className="flex flex-col gap-1.5" data-strategy-rd-robustness-steps="">
      {robustnessSteps(runs).map((st, i) => (
        <li key={st.title}>
          <strong>
            {i + 1}. {st.title}.
          </strong>{" "}
          {st.body}
        </li>
      ))}
    </ol>
  );
}

/** Before a run: what the result will tell you, as the three measures it reports. */
function RobustnessChecks(): JSX.Element {
  const rows = [
    {
      label: "Same trades",
      body: "How many of every 10 trades come back identical when the prices are nudged.",
    },
    { label: "Return range", body: "Where 9 in 10 re-runs' returns land, and whether that range crosses zero." },
    { label: "With typical costs", body: "The return after the instrument's usual spread and overnight swap." },
  ];
  return (
    <div
      className="grid overflow-hidden rounded-[6px] border border-border sm:grid-cols-3 [&>*]:-mb-px [&>*]:-mr-px [&>*]:border-b [&>*]:border-r [&>*]:border-border"
      data-strategy-rd-robustness-checks=""
    >
      {rows.map((row) => (
        <div key={row.label} className="flex flex-col gap-1.5 bg-background p-4 dark:bg-sidebar">
          <span className={strategyRdCaptionClassName}>{row.label}</span>
          <span className={cn(strategyRdBodyTextClassName, "text-xs leading-relaxed")}>{row.body}</span>
        </div>
      ))}
    </div>
  );
}

/** While the test runs: the app's tile loader in place of the content, with what is happening. */
function RobustnessRunning(): JSX.Element {
  return (
    <div className="flex flex-col gap-3" data-strategy-rd-robustness-running="">
      <div
        className="relative h-[220px] w-full overflow-hidden rounded-[6px] border border-border bg-background dark:bg-sidebar"
        aria-busy="true"
        aria-label="Running the robustness test"
      >
        <EmbeddedLoadingChart status={RUNNING_LABEL} />
      </div>
      <div className="flex items-center gap-2">
        <Spinner className="text-[color:var(--brand-tropical-surf)]" />
        <span className={strategyRdBodyTextClassName}>
          Nudging the prices, re-running every copy and adding typical costs. This usually takes about a minute. The
          result appears here when it is ready.
        </span>
      </div>
    </div>
  );
}

/** Ten squares: how many of every 10 trades survive a re-run. */
function KeptGrid({ share }: { share: number }): JSX.Element {
  const kept = Math.max(0, Math.min(10, Math.round(share * 10)));
  return (
    <div className="my-1.5 flex gap-1" aria-hidden data-strategy-rd-kept-grid="">
      {Array.from({ length: 10 }, (_, i) => (
        <span
          key={i}
          className="h-3 flex-1 rounded-[2px]"
          style={{ background: i < kept ? "var(--brand-tropical-surf)" : "var(--muted)" }}
        />
      ))}
    </div>
  );
}

/** The 9-in-10 return range on a number line with zero marked; the dot is this backtest. */
function ReturnLine({ lo, hi, base }: { lo: number; hi: number; base: number | null }): JSX.Element {
  const min = Math.min(lo, 0, base ?? lo);
  const max = Math.max(hi, 0, base ?? hi);
  const span = max - min || 1;
  const x = (v: number) => `${((v - min) / span) * 100}%`;
  return (
    <div className="relative my-2 h-6" aria-hidden data-strategy-rd-return-line="">
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
      <div
        className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full"
        style={{
          left: x(lo),
          width: `${((hi - lo) / span) * 100}%`,
          background: `linear-gradient(90deg, ${lo < 0 ? PNL_NEGATIVE_COLOR : PNL_POSITIVE_COLOR}, ${hi < 0 ? PNL_NEGATIVE_COLOR : PNL_POSITIVE_COLOR})`,
          opacity: 0.45,
        }}
      />
      <div className="absolute top-0 h-full w-px bg-muted-foreground/60" style={{ left: x(0) }} />
      <span className="absolute -bottom-3 -translate-x-1/2 text-[9px] text-muted-foreground" style={{ left: x(0) }}>
        0%
      </span>
      {base != null ? (
        <div
          className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background"
          style={{ left: x(base), background: "var(--brand-tropical-surf)" }}
        />
      ) : null}
    </div>
  );
}

/** Return as tested vs with typical costs, as two bars from zero. */
function CostBars({ asTested, withCosts }: { asTested: number | null; withCosts: number | null }): JSX.Element | null {
  if (asTested == null && withCosts == null) return null;
  const rows = [
    { label: "As tested", v: asTested },
    { label: "With costs", v: withCosts },
  ];
  const max = Math.max(0.5, ...rows.map((r) => Math.abs(r.v ?? 0)));
  return (
    <div className="my-1.5 flex flex-col gap-1" aria-hidden data-strategy-rd-cost-bars="">
      {rows.map((r) => {
        const v = r.v ?? 0;
        return (
          <div key={r.label} className="flex items-center gap-2 text-[10px]">
            <span className="w-16 shrink-0 text-muted-foreground">{r.label}</span>
            <div className="relative h-2.5 flex-1">
              <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
              <div
                className="absolute inset-y-0 rounded-[2px]"
                style={{
                  width: `${(Math.abs(v) / max) * 50}%`,
                  ...(v >= 0 ? { left: "50%" } : { right: "50%" }),
                  background: v >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const FRAGILE_TONE: Record<string, StrategyRdTone> = { target: "good", stop: "bad", liquidated: "bad", exit: "warn" };

/** The fragile trades in the app's data table, like the Trades tab: sortable, paged, a row opens the trade. */
function FragileTradeList({
  trades,
  total,
  timeZone,
  currency,
  onSelect,
}: {
  trades: StrategyRdFragileTrade[];
  total: number;
  timeZone: string;
  currency: string | null;
  onSelect?: (trade: StrategyRdFragileTrade) => void;
}): JSX.Element {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: FRAGILE_PAGE_SIZE });
  const pct = (v: number | null | undefined) => (v != null ? `${v}%` : NA);
  const columns: ColumnDef<StrategyRdFragileTrade, unknown>[] = useMemo(
    () => [
      {
        id: "entry",
        accessorFn: (t) => Date.parse(t.entry_time),
        header: ({ column }) => <DataTableColumnHeader column={column} title="Entry Date" />,
        cell: ({ row }) => (
          <TradeLogSortableCell>{formatTradeDateTimeForDisplay(row.original.entry_time, timeZone)}</TradeLogSortableCell>
        ),
      },
      {
        id: "side",
        accessorFn: (t) => t.side,
        header: "Type",
        enableSorting: false,
        cell: ({ row }) => <TradePositionSideBadge side={row.original.side === "short" ? "short" : "long"} />,
      },
      {
        id: "result",
        accessorFn: (t) => t.result ?? "",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Result" />,
        cell: ({ row }) =>
          row.original.result ? (
            <StrategyRdBadge tone={FRAGILE_TONE[row.original.result] ?? "neutral"}>
              {strategyRdOutcomeLabel(row.original.result)}
            </StrategyRdBadge>
          ) : (
            <TradeLogSortableCell>{NA}</TradeLogSortableCell>
          ),
      },
      {
        id: "pnl",
        accessorFn: (t) => t.pnl ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="P&L" />,
        cell: ({ row }) => {
          const v = row.original.pnl;
          const color = v != null && v > 0 ? PNL_POSITIVE_COLOR : v != null && v < 0 ? PNL_NEGATIVE_COLOR : undefined;
          return (
            <TradeLogSortableCell className="font-medium">
              <span style={color ? { color } : undefined}>{formatStrategyRdMoney(v, currency) ?? NA}</span>
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "kept",
        accessorFn: (t) => t.kept_pct ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Kept in runs" />,
        cell: ({ row }) => <TradeLogSortableCell className="font-medium">{pct(row.original.kept_pct)}</TradeLogSortableCell>,
      },
      {
        id: "withinBar",
        accessorFn: (t) => t.within_1_bar_pct ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Within a bar" />,
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">{pct(row.original.within_1_bar_pct)}</TradeLogSortableCell>
        ),
      },
    ],
    [currency, timeZone]
  );
  return (
    <div data-strategy-rd-fragile-trades="" data-total={total}>
      <DataTable
        columns={columns}
        data={trades}
        getRowId={(t) => `${t.side}:${t.entry_time}`}
        onRowClick={onSelect}
        pagination={pagination}
        onPaginationChange={setPagination}
        showPagination={trades.length > FRAGILE_PAGE_SIZE}
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No fragile trades."
      />
    </div>
  );
}

const FRAGILE_PAGE_SIZE = 10;
